// src/pages/Admin/EditFruit.jsx
import React, { useState, useEffect } from 'react';
import {
    Container, Paper, Typography, Box, Button, Grid, TextField,
    FormControl, InputLabel, Select, MenuItem, Chip, Card, CardContent,
    Alert, IconButton, InputAdornment, Switch, FormControlLabel,
    Divider, Collapse, Tooltip, List, ListItem, ListItemText,
    CircularProgress, alpha
} from '@mui/material';
import {
    ArrowBack, AddPhotoAlternate, Save, Cancel, CloudUpload,
    Add, ExpandMore, ExpandLess, ClearAll, Info,
    LocalOffer, Star, Numbers, Flag, CropSquare,
    LocalShipping, Language, Grading
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';

const EditFruit = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // ✅ get fruit ID from URL

    const [loading, setLoading]           = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true); // ✅ loading existing data
    const [imageFile, setImageFile]       = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        category: '',
        varieties: [],
        grades: [],
        counts: [],
        origins: [],
        sizes: [],
        containerTypes: [],
        ports: [],
        moq: '1 container',
        price_per_unit: '',
        description: '',
        active: true,
        seasonality: 'year-round',
        taste_profile: '',
        nutritional_info: '',
        storage_requirements: '',
        shelf_life: '',
        certifications: [],
    });

    const [inputValues, setInputValues] = useState({
        varieties: '', grades: '', counts: '', origins: '',
        sizes: '', containerTypes: '', ports: '', certifications: '',
    });

    // ── Static options ──────────────────────────────────────────────────────
    const categories = [
        'Apple','Mango','Banana','Orange','Grapes','Pineapple',
        'Berries','Melon','Citrus','Tropical','Exotic','Other',
    ];

    const moqOptions = [
        '1 container','2 containers','3 containers','4 containers',
        '5 containers','10 containers','Full Load',
    ];

    const seasonalityOptions = [
        { value: 'year-round', label: 'Year-round' },
        { value: 'summer',     label: 'Summer' },
        { value: 'winter',     label: 'Winter' },
        { value: 'spring',     label: 'Spring' },
        { value: 'autumn',     label: 'Autumn' },
        { value: 'monsoon',    label: 'Monsoon' },
    ];

    const suggestions = {
        varieties:      ['Fuji','Gala','Granny Smith','Red Delicious','Golden Delicious','Pink Lady'],
        grades:         ['A','B','C','Premium','Standard','Commercial','Export Quality'],
        counts:         ['80-100','100-120','120-140','140-160','160-180','180-200'],
        sizes:          ['Small','Medium','Large','Extra Large','Jumbo','Mixed'],
        containerTypes: ['20 FT Reefer Container','40 FT Reefer Container','20 FT Dry Container'],
        ports:          ['Singapore (SG SIN)','Shanghai (CN SHA)','Rotterdam (NL RTM)','Dubai (AE DXB)'],
        origins:        [],
        certifications: [],
    };

    const fieldIcons = {
        varieties: <LocalOffer fontSize="small" />, grades: <Star fontSize="small" />,
        counts: <Numbers fontSize="small" />,       origins: <Flag fontSize="small" />,
        sizes: <CropSquare fontSize="small" />,     containerTypes: <LocalShipping fontSize="small" />,
        ports: <Language fontSize="small" />,       certifications: <Grading fontSize="small" />,
    };

    const fieldLabels = {
        varieties: 'Varieties', grades: 'Grades', counts: 'Counts',
        origins: 'Origin Countries', sizes: 'Sizes',
        containerTypes: 'Container Types', ports: 'Ports of Discharge',
        certifications: 'Certifications',
    };

    const fieldDescriptions = {
        varieties:      'Enter different fruit varieties (e.g., Fuji, Gala)',
        grades:         'Enter quality grades (e.g., A, Premium)',
        counts:         'Enter count ranges (e.g., 80-100, 100-120)',
        origins:        'Enter origin countries (e.g., India, USA)',
        sizes:          'Enter size categories (e.g., Small, Large)',
        containerTypes: 'Enter container types (e.g., 20 FT Reefer)',
        ports:          'Enter port names (e.g., Singapore, Shanghai)',
        certifications: 'Enter certifications (e.g., Organic, ISO)',
    };

    // ── Helper: safely parse JSON array fields from backend ─────────────────
    const parseArrayField = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            return typeof value === 'string' && value.trim()
                ? value.split(',').map(s => s.trim()).filter(Boolean)
                : [];
        }
    };

    // ── Fetch existing fruit on mount ────────────────────────────────────────
    useEffect(() => {
        const fetchFruit = async () => {
            try {
                setFetchLoading(true);
                const response = await adminAPI.get(`/admin/fruits/${id}`);
                const fruit = response.data;

                setFormData({
                    name:                 fruit.name            || '',
                    category:             fruit.category        || '',
                    varieties:            parseArrayField(fruit.variety   || fruit.varieties),
                    grades:               parseArrayField(fruit.grade     || fruit.grades),
                    counts:               parseArrayField(fruit.count     || fruit.counts),
                    origins:              parseArrayField(fruit.origin_country || fruit.origins),
                    sizes:                parseArrayField(fruit.size      || fruit.sizes),
                    containerTypes:       parseArrayField(fruit.container_type || fruit.containerTypes),
                    ports:                parseArrayField(fruit.port_of_discharge || fruit.ports),
                    moq:                  fruit.moq             || '1 container',
                    price_per_unit:       fruit.price_per_unit  != null ? String(fruit.price_per_unit) : '',
                    description:          fruit.description     || '',
                    active:               fruit.active          !== undefined ? fruit.active : true,
                    seasonality:          fruit.seasonality     || 'year-round',
                    taste_profile:        fruit.taste_profile   || '',
                    nutritional_info:     fruit.nutritional_info || '',
                    storage_requirements: fruit.storage_requirements || '',
                    shelf_life:           fruit.shelf_life      || '',
                    certifications:       parseArrayField(fruit.certifications),
                });

                // ✅ Show existing image preview
                if (fruit.image_url || fruit.image_path) {
                    setImagePreview(
                        fruit.image_url ||
                        `${(process.env.REACT_APP_BACKEND_URL||process.env.REACT_APP_API_URL?.replace('/api','')||'http://localhost:5000').replace(/\/$/,'')}/static/uploads/fruits/${fruit.image_path}`
                    );
                }
            } catch (error) {
                console.error('Error fetching fruit:', error);
                toast.error('Failed to load fruit data');
                navigate('/admin/fruits');
            } finally {
                setFetchLoading(false);
            }
        };

        if (id) fetchFruit();
    }, [id, navigate]);

    // ── Form handlers ────────────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleInputChange = (field, value) => {
        setInputValues(prev => ({ ...prev, [field]: value }));
    };

    const handleAddItem = (field, value) => {
        const trimmed = value.trim();
        if (!trimmed) { toast.error(`Please enter a ${fieldLabels[field].toLowerCase()}`); return; }
        if (formData[field].includes(trimmed)) { toast.error('This value already exists'); return; }
        setFormData(prev => ({ ...prev, [field]: [...prev[field], trimmed] }));
        setInputValues(prev => ({ ...prev, [field]: '' }));
        toast.success(`Added ${trimmed}`);
    };

    const handleRemoveItem = (field, item) => {
        setFormData(prev => ({ ...prev, [field]: prev[field].filter(i => i !== item) }));
    };

    const handleClearAll = (field) => {
        setFormData(prev => ({ ...prev, [field]: [] }));
        toast.success(`Cleared all ${fieldLabels[field].toLowerCase()}`);
    };

    const handleKeyPress = (field, e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            const val = inputValues[field].trim();
            if (val) handleAddItem(field, val.replace(',', ''));
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    // ── Submit ───────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        try {
            setLoading(true);

            const errors = [];
            if (!formData.name)                  errors.push('Fruit name is required');
            if (!formData.category)              errors.push('Category is required');
            if (formData.varieties.length === 0) errors.push('At least one variety is required');
            if (formData.grades.length === 0)    errors.push('At least one grade is required');
            if (formData.origins.length === 0)   errors.push('At least one origin country is required');

            if (errors.length > 0) {
                errors.forEach(e => toast.error(e));
                return;
            }

            const submitData = new FormData();

            // Append array fields as JSON strings
            const arrayFields = ['varieties','grades','counts','origins','sizes','containerTypes','ports','certifications'];
            Object.keys(formData).forEach(key => {
                if (arrayFields.includes(key)) {
                    submitData.append(key, JSON.stringify(formData[key]));
                } else {
                    submitData.append(key, formData[key]);
                }
            });

            // Only append image if a new one was selected
            if (imageFile) {
                submitData.append('image', imageFile);
            }

            // ✅ PUT request to update endpoint
            await adminAPI.put(`/admin/fruits/${id}`, submitData);
            toast.success('Fruit updated successfully!');
            navigate('/admin/fruits');

        } catch (error) {
            console.error('Error updating fruit:', error);
            toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to update fruit');
        } finally {
            setLoading(false);
        }
    };

    // ── Selection field renderer ─────────────────────────────────────────────
    const renderSelectionField = (field, required = false) => {
        const items          = formData[field] || [];
        const inputValue     = inputValues[field] || '';
        const suggestionList = suggestions[field]  || [];

        return (
            <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {fieldIcons[field]}
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                    {fieldLabels[field]} {required && '*'}
                                </Typography>
                            </Box>
                            {items.length > 0 && (
                                <Tooltip title={`Clear all ${fieldLabels[field].toLowerCase()}`}>
                                    <IconButton size="small" onClick={() => handleClearAll(field)} color="error">
                                        <ClearAll />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>

                        {/* Input */}
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                fullWidth size="small"
                                placeholder={`Add ${fieldLabels[field].toLowerCase()}...`}
                                value={inputValue}
                                onChange={(e) => handleInputChange(field, e.target.value)}
                                onKeyPress={(e) => handleKeyPress(field, e)}
                                InputProps={{
                                    endAdornment: inputValue && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => handleAddItem(field, inputValue)} color="primary">
                                                <Add />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                {fieldDescriptions[field]}. Press Enter or comma to add.
                            </Typography>
                        </Box>

                        {/* Suggestions */}
                        {suggestionList.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                                    Quick add:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {suggestionList.map((s, idx) => (
                                        <Chip key={idx} label={s} size="small" onClick={() => handleAddItem(field, s)}
                                            variant="outlined" sx={{ cursor: 'pointer' }} />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Selected items */}
                        {items.length > 0 ? (
                            <Box sx={{
                                p: 1, border: '1px solid', borderColor: 'divider',
                                borderRadius: 1, maxHeight: 120, overflowY: 'auto',
                                bgcolor: alpha('#1976d2', 0.03),
                            }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {items.map((item, i) => (
                                        <Chip key={i} label={item} onDelete={() => handleRemoveItem(field, item)}
                                            color="primary" variant="filled" size="small"
                                            sx={{ m: 0.5, '& .MuiChip-deleteIcon': { color: 'white' } }} />
                                    ))}
                                </Box>
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    {items.length} item(s) selected
                                </Typography>
                            </Box>
                        ) : (
                            <Alert severity="info" icon={<Info />}
                                sx={{ backgroundColor: 'background.default', border: '1px dashed', borderColor: 'info.main' }}>
                                No {fieldLabels[field].toLowerCase()} added yet.
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    // ── Loading state ────────────────────────────────────────────────────────
    if (fetchLoading) {
        return (
            <Container sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }} color="text.secondary">
                    Loading fruit data...
                </Typography>
            </Container>
        );
    }

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/fruits')}
                variant="outlined" sx={{ mb: 3 }}>
                Back to Fruits
            </Button>

            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                {/* ✅ Title shows "Edit Fruit" not "Add New Fruit" */}
                <Typography variant="h4" gutterBottom color="primary">
                    Edit Fruit — {formData.name}
                </Typography>
                <Typography variant="body1" color="textSecondary" paragraph>
                    Update the fruit details below. Fields marked with * are required.
                </Typography>

                <Grid container spacing={3}>
                    {/* ── Basic Information ── */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                            <Box component="span" sx={{ borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5 }}>
                                Basic Information
                            </Box>
                        </Typography>
                        <Divider />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Fruit Name *" name="name"
                            value={formData.name} onChange={handleChange}
                            required variant="outlined" size="small" />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>Category *</InputLabel>
                            <Select name="category" value={formData.category}
                                onChange={handleChange} label="Category *">
                                <MenuItem value=""><em>Select Category</em></MenuItem>
                                {categories.map(c => (
                                    <MenuItem key={c} value={c}>{c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" name="description"
                            value={formData.description} onChange={handleChange}
                            multiline rows={3} variant="outlined" size="small"
                            helperText="Provide a detailed description of the fruit" />
                    </Grid>

                    {/* ── Product Specifications ── */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                            <Box component="span" sx={{ borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5 }}>
                                Product Specifications
                            </Box>
                        </Typography>
                        <Typography variant="caption" color="textSecondary" gutterBottom>
                            Type and press Enter to add items
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid container spacing={2} sx={{ px: 1.5 }}>
                        {renderSelectionField('varieties', true)}
                        {renderSelectionField('grades', true)}
                        {renderSelectionField('origins', true)}
                        {renderSelectionField('sizes')}
                        {renderSelectionField('counts')}
                        {renderSelectionField('containerTypes')}
                        {renderSelectionField('ports')}
                    </Grid>

                    {/* ── Pricing ── */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                            <Box component="span" sx={{ borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5 }}>
                                Pricing & Order Details
                            </Box>
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Price per Unit (USD)" name="price_per_unit"
                            type="number" value={formData.price_per_unit} onChange={handleChange}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                inputProps: { min: 0, step: 0.01 },
                            }}
                            variant="outlined" size="small" helperText="Base price per unit" />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Minimum Order Quantity</InputLabel>
                            <Select name="moq" value={formData.moq} onChange={handleChange}
                                label="Minimum Order Quantity">
                                {moqOptions.map(o => (
                                    <MenuItem key={o} value={o}>{o}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* ── Image ── */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                            <Box component="span" sx={{ borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5 }}>
                                Product Image
                            </Box>
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{
                            border: '2px dashed', borderColor: 'divider', borderRadius: 2,
                            p: 3, textAlign: 'center', backgroundColor: 'background.default',
                            '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
                        }}>
                            <input accept="image/*" style={{ display: 'none' }}
                                id="edit-image-upload" type="file" onChange={handleImageUpload} />
                            <label htmlFor="edit-image-upload">
                                <Button variant="contained" component="span"
                                    startIcon={<CloudUpload />} sx={{ mb: 2 }}>
                                    {imageFile ? 'Change Image' : 'Upload New Image'}
                                </Button>
                            </label>

                            {imagePreview ? (
                                <Box sx={{ mt: 2 }}>
                                    <img src={imagePreview} alt="Preview"
                                        style={{ maxWidth: 300, maxHeight: 300, objectFit: 'cover',
                                                 borderRadius: 8, border: '1px solid #ddd' }} />
                                    <Typography variant="caption" color="textSecondary"
                                        sx={{ mt: 1, display: 'block' }}>
                                        {imageFile ? 'New image selected' : 'Current image'}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ mt: 2 }}>
                                    <AddPhotoAlternate sx={{ fontSize: 60, color: 'text.disabled' }} />
                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                        No image. Upload a product image.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    {/* ── Advanced Options ── */}
                    <Grid item xs={12}>
                        <Button onClick={() => setShowAdvanced(!showAdvanced)}
                            startIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
                            variant="outlined" sx={{ mt: 2 }} fullWidth>
                            {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
                        </Button>

                        <Collapse in={showAdvanced}>
                            <Box sx={{ mt: 3, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom>Advanced Product Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Seasonality</InputLabel>
                                            <Select name="seasonality" value={formData.seasonality}
                                                onChange={handleChange} label="Seasonality">
                                                {seasonalityOptions.map(o => (
                                                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth label="Shelf Life" name="shelf_life"
                                            value={formData.shelf_life} onChange={handleChange}
                                            variant="outlined" size="small" helperText="e.g., 14 days" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Taste Profile" name="taste_profile"
                                            value={formData.taste_profile} onChange={handleChange}
                                            multiline rows={2} variant="outlined" size="small" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Nutritional Information" name="nutritional_info"
                                            value={formData.nutritional_info} onChange={handleChange}
                                            multiline rows={3} variant="outlined" size="small"
                                            helperText="Key nutritional facts per 100g" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Storage Requirements" name="storage_requirements"
                                            value={formData.storage_requirements} onChange={handleChange}
                                            multiline rows={2} variant="outlined" size="small" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        {renderSelectionField('certifications')}
                                    </Grid>
                                </Grid>
                            </Box>
                        </Collapse>
                    </Grid>

                    {/* ── Status ── */}
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ mt: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="subtitle1" gutterBottom>Product Status</Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Active products are visible to buyers
                                        </Typography>
                                    </Box>
                                    <FormControlLabel
                                        control={
                                            <Switch checked={formData.active} color="primary"
                                                onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))} />
                                        }
                                        label={formData.active ? 'Active' : 'Inactive'}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* ── Summary ── */}
                    <Grid item xs={12}>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Summary</Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemText primary="Basic Information"
                                        secondary={`Name: ${formData.name || 'Not set'}, Category: ${formData.category || 'Not set'}`} />
                                </ListItem>
                                <ListItem>
                                    <ListItemText primary="Specifications"
                                        secondary={`Varieties: ${formData.varieties.length}, Grades: ${formData.grades.length}, Origins: ${formData.origins.length}`} />
                                </ListItem>
                                <ListItem>
                                    <ListItemText primary="Image"
                                        secondary={imageFile ? 'New image selected' : imagePreview ? 'Using existing image' : 'No image'} />
                                </ListItem>
                            </List>
                        </Alert>
                    </Grid>
                </Grid>

                {/* ── Action Buttons ── */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3,
                           borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button onClick={() => navigate('/admin/fruits')} variant="outlined"
                        startIcon={<Cancel />} size="large">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary"
                        disabled={loading} sx={{ minWidth: 150 }} size="large" startIcon={<Save />}>
                        {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default EditFruit;